import { useState, useEffect } from 'react';
import { getProjects } from '../api/projects';
import { Project, PaginatedResponse, ProjectListParams } from '../types/index';

interface UseProjectsReturn {
  projects: Project[];
  total: number;
  isLoading: boolean;
  error: Error | null;
  refetch: (params?: ProjectListParams) => Promise<void>;
}

export const useProjects = (params?: ProjectListParams): UseProjectsReturn => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = async (newParams?: ProjectListParams) => {
    setIsLoading(true);
    setError(null);

    try {
      const data: PaginatedResponse<Project> = await getProjects(newParams || params);
      setProjects(data.items);
      setTotal(data.total);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch projects');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, [params]);

  return { projects, total, isLoading, error, refetch };
};

// Bug #334: this was `// API call stub` returning `Promise.resolve()`, so the
// Add project modal closed as though it had saved and nothing was ever
// created — the identical defect #331 fixed on Companies. Re-exported from
// the API module rather than reimplemented, so there is one code path and
// `ProjectList.tsx` keeps importing from where it already did.
export { createProject, updateProject, deactivateProject } from '../api/projects';
